import type { Asset } from '@elara/client-core/lib/asset';
import type { ImageProcessor } from '@elara/client-core/lib/image-processor';

import { assetToBlob } from '@elara/client-core/lib/asset';

/** Longest edge (px) of a generated thumbnail; the other edge scales to keep aspect ratio. */
const MAX_THUMBNAIL_SIZE = 200;
/** JPEG quality for the generated thumbnail (0–1). */
const THUMBNAIL_QUALITY = 0.8;

/**
 * Web `ImageProcessor` implementation (ELARA-692) backed by `FileReader` +
 * `<canvas>`. Moved verbatim out of `FileUploadState.generateThumbnail` so the
 * upload state no longer reaches for DOM globals.
 *
 * This file is web-only and must not be imported by `@elara/mobile`; React
 * Native composes an `expo-image-manipulator`-backed `ImageProcessor` instead.
 */
export function createWebImageProcessor(): ImageProcessor {
  return {
    async generateThumbnail(asset: Asset): Promise<string | undefined> {
      if (!asset.mimeType.startsWith('image/')) return undefined;
      const blob = await assetToBlob(asset);

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('error', () => resolve(undefined));
        reader.addEventListener('load', (e) => {
          const img = new Image();
          img.addEventListener('error', () => resolve(undefined));
          img.addEventListener('load', () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve(undefined);
              return;
            }

            // Scale the longest edge down to MAX_THUMBNAIL_SIZE, preserving aspect ratio.
            let width = img.width;
            let height = img.height;
            if (width > height) {
              if (width > MAX_THUMBNAIL_SIZE) {
                height *= MAX_THUMBNAIL_SIZE / width;
                width = MAX_THUMBNAIL_SIZE;
              }
            } else {
              if (height > MAX_THUMBNAIL_SIZE) {
                width *= MAX_THUMBNAIL_SIZE / height;
                height = MAX_THUMBNAIL_SIZE;
              }
            }
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              (thumbnailBlob) => {
                if (!thumbnailBlob) {
                  resolve(undefined);
                  return;
                }
                const thumbnailReader = new FileReader();
                thumbnailReader.addEventListener('error', () => resolve(undefined));
                thumbnailReader.addEventListener('load', () => resolve(thumbnailReader.result as string));
                thumbnailReader.readAsDataURL(thumbnailBlob);
              },
              'image/jpeg',
              THUMBNAIL_QUALITY,
            );
          });
          img.src = e.target?.result as string;
        });
        reader.readAsDataURL(blob);
      });
    },
  };
}
